import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { StudyCard } from "@/lib/types";
import { api } from "@/lib/api";
import { useProfileStore, useActiveProfile } from "@/state/profile";
import { navigate } from "@/lib/router";
import { burstConfetti, sfxCorrect, sfxWrong } from "@/lib/rewards";
import { cn } from "@/lib/cn";
import { VerbTimeline } from "@/components/VerbTimeline";
import { todayKey } from "@/lib/date";
import { SpeakButton } from "@/components/SpeakButton";

type Status = "loading" | "ready" | "error" | "empty" | "done";

export function StudyPage() {
  const profileId = useProfileStore((s) => s.activeId);
  const profile = useActiveProfile();
  const [cards, setCards] = useState<StudyCard[]>([]);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [status, setStatus] = useState<Status>("loading");
  const [error, setError] = useState<string | null>(null);
  const [counts, setCounts] = useState({ knew: 0, later: 0 });

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    api.study
      .queue(profileId, todayKey())
      .then(({ cards }) => {
        if (cancelled) return;
        setCards(cards);
        setStatus(cards.length ? "ready" : "empty");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "load failed");
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  const current = cards[idx];

  const advance = useCallback(() => {
    setFlipped(false);
    setIdx((i) => {
      const next = i + 1;
      if (next >= cards.length) {
        setStatus("done");
      }
      return next;
    });
  }, [cards.length]);

  async function rate(rating: "knew" | "still_learning") {
    if (!current) return;
    try {
      await api.study.rate(profileId, {
        item_key: current.item_key,
        game: current.game,
        rating,
        date_key: todayKey(),
      });
    } catch {
      /* non-fatal — UI advances anyway */
    }
    if (rating === "knew") {
      setCounts((c) => ({ ...c, knew: c.knew + 1 }));
      sfxCorrect(counts.knew + 1);
      burstConfetti(profile.color);
    } else {
      setCounts((c) => ({ ...c, later: c.later + 1 }));
      sfxWrong();
    }
    advance();
  }

  return (
    <div className="min-h-[100dvh] w-full max-w-lg mx-auto flex flex-col safe-pt safe-pb px-4">
      <header className="flex items-center gap-3 pt-1 pb-3">
        <button
          aria-label="Back"
          onClick={() => navigate({ name: "home" })}
          className="h-10 w-10 rounded-2xl grid place-items-center bg-brand-surface border border-brand-border shadow-chunk"
        >
          ←
        </button>
        <div className="flex-1">
          <div className="font-display text-lg leading-none">Repaso</div>
          <div className="text-xs text-brand-muted uppercase tracking-widest font-extrabold">
            {status === "ready" && cards.length > 0
              ? `${Math.min(idx + 1, cards.length)} / ${cards.length}`
              : "tarjetas"}
          </div>
        </div>
        {status === "ready" && (
          <div className="flex gap-1 text-xs font-black shrink-0">
            <span className="pill px-2 bg-brand-mint/20 border-brand-mint/40 text-brand-ink">
              ✓ {counts.knew}
            </span>
            <span className="pill px-2 bg-brand-rose/15 border-brand-rose/40 text-brand-rose">
              ↻ {counts.later}
            </span>
          </div>
        )}
      </header>

      <main className="flex-1 flex flex-col">
        {status === "loading" && <Loading />}
        {status === "error" && <ErrorView message={error ?? "error"} />}
        {status === "empty" && <EmptyState />}
        {status === "done" && <DoneState counts={counts} />}
        {status === "ready" && current && (
          <Flashcard
            card={current}
            flipped={flipped}
            onFlip={() => setFlipped((f) => !f)}
          />
        )}
      </main>

      {status === "ready" && current && (
        <div className="grid grid-cols-2 gap-3 mt-4">
          <button
            onClick={() => rate("still_learning")}
            className="chunk-btn bg-brand-surface border-2 border-brand-rose/50 text-brand-rose"
          >
            Todavía no
          </button>
          <button
            onClick={() => rate("knew")}
            className="chunk-btn bg-brand-mint text-brand-ink"
          >
            Lo sé
          </button>
        </div>
      )}
    </div>
  );
}

function Flashcard({
  card,
  flipped,
  onFlip,
}: {
  card: StudyCard;
  flipped: boolean;
  onFlip: () => void;
}) {
  const tint =
    card.kind === "el_la"
      ? "from-brand-saffron/30 to-brand-coral/20"
      : card.kind === "conj"
        ? "from-brand-mint/25 to-brand-violet/15"
        : "from-brand-violet/25 to-brand-rose/15";
  return (
    <button
      type="button"
      onClick={onFlip}
      className="relative flex-1 min-h-[260px] w-full [perspective:1200px] focus:outline-none"
      aria-label={flipped ? "Volver" : "Mostrar respuesta"}
    >
      <AnimatePresence mode="wait" initial={false}>
        {!flipped ? (
          <motion.div
            key={`front-${card.item_key}`}
            initial={{ rotateY: -90, opacity: 0 }}
            animate={{ rotateY: 0, opacity: 1 }}
            exit={{ rotateY: 90, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className={cn(
              "absolute inset-0 chunk p-6 flex flex-col items-center justify-center text-center bg-gradient-to-br",
              tint,
            )}
            style={{ backfaceVisibility: "hidden" }}
          >
            <span className="pill mb-3">
              {card.kind === "el_la"
                ? "El o La"
                : card.kind === "conj"
                  ? "Conjugación"
                  : "Vocabulario"}
            </span>
            <div className="font-display text-3xl sm:text-4xl leading-tight break-words">
              {card.prompt}
            </div>
            {card.meta && (
              <div className="mt-3 text-sm text-brand-muted font-bold">
                {card.meta}
              </div>
            )}
            {card.english && card.kind === "el_la" && (
              <div className="mt-1 text-xs text-brand-muted font-bold">
                {card.english}
              </div>
            )}
            <div className="mt-auto text-xs text-brand-muted font-black uppercase tracking-widest pt-4">
              Toca para ver la respuesta
            </div>
          </motion.div>
        ) : (
          <motion.div
            key={`back-${card.item_key}`}
            initial={{ rotateY: -90, opacity: 0 }}
            animate={{ rotateY: 0, opacity: 1 }}
            exit={{ rotateY: 90, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 chunk p-5 sm:p-6 flex flex-col items-center justify-start text-center bg-brand-surface overflow-y-auto"
            style={{ backfaceVisibility: "hidden" }}
          >
            <span className="pill mb-3">Respuesta</span>
            <div className="font-display text-3xl sm:text-4xl leading-tight break-words text-brand-mint">
              {card.answer}
            </div>
            <div className="mt-3"><SpeakButton text={card.answer} /></div>
            {card.english && card.kind !== "el_la" && (
              <div className="mt-2 text-sm text-brand-muted font-bold">
                {card.english}
              </div>
            )}
            {card.note && (
              <p className="mt-4 text-sm bg-brand-saffron/20 rounded-2xl px-3 py-2 font-bold text-brand-ink">
                {card.note}
              </p>
            )}
            {card.kind === "conj" && card.timeline?.length ? (
              <div className="mt-4 w-full">
                <VerbTimeline
                  timeline={card.timeline}
                  gerund={card.gerund}
                  compact
                />
              </div>
            ) : null}
            <div className="mt-auto text-xs text-brand-muted font-black uppercase tracking-widest pt-4">
              fallado {card.times_wrong}×
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </button>
  );
}

function Loading() {
  return (
    <div className="flex-1 grid place-items-center">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, duration: 1.8, ease: "linear" }}
        className="text-5xl"
      >
        🗂️
      </motion.div>
    </div>
  );
}

function ErrorView({ message }: { message: string }) {
  return (
    <div className="flex-1 grid place-items-center text-center px-4">
      <div>
        <div className="text-5xl">🙈</div>
        <div className="mt-2 font-display text-2xl">Algo falló</div>
        <p className="mt-1 text-sm text-brand-muted font-bold">{message}</p>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex-1 grid place-items-center text-center px-4">
      <div>
        <div className="text-6xl">🌿</div>
        <div className="mt-3 font-display text-2xl">Nada que repasar</div>
        <p className="mt-1 text-sm text-brand-muted font-bold max-w-xs mx-auto">
          No tienes errores pendientes ahora mismo. Sigue jugando y las
          palabras difíciles aparecerán aquí.
        </p>
      </div>
    </div>
  );
}

function DoneState({ counts }: { counts: { knew: number; later: number } }) {
  return (
    <div className="flex-1 grid place-items-center text-center px-4">
      <div>
        <div className="text-6xl">🎉</div>
        <div className="mt-3 font-display text-2xl">¡Repaso completo!</div>
        <p className="mt-2 text-sm text-brand-muted font-bold">
          <span className="text-brand-mint">{counts.knew} aprendidas</span> ·{" "}
          <span className="text-brand-rose">{counts.later} para más tarde</span>
        </p>
        <button
          onClick={() => navigate({ name: "home" })}
          className="chunk-btn-primary mt-5 px-6"
        >
          Volver al inicio
        </button>
      </div>
    </div>
  );
}
